import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { expValEquals } from '@confluence/feature-experiments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useReadTime } from '@confluence/read-time/entry-points/useReadTime';

import { useAIRovoButtonEnablement } from '../ai-smart-button/useAIRovoButtonEnablement';

import type {
	useDynamicSplitButtonLastModifiedDateQuery$data,
	useDynamicSplitButtonLastModifiedDateQuery$variables,
} from './__generated__/useDynamicSplitButtonLastModifiedDateQuery.graphql';
import type {
	useDynamicSplitButtonCatchupLastViewedTimeQuery$data,
	useDynamicSplitButtonCatchupLastViewedTimeQuery$variables,
	CatchupContentType,
} from './__generated__/useDynamicSplitButtonCatchupLastViewedTimeQuery.graphql';
import type {
	useDynamicSplitButtonContentHistoryQuery$data,
	useDynamicSplitButtonContentHistoryQuery$variables,
} from './__generated__/useDynamicSplitButtonContentHistoryQuery.graphql';

const useDynamicSplitButtonCatchupLastViewedTimeQuery = graphql`
	query useDynamicSplitButtonCatchupLastViewedTimeQuery(
		$contentId: ID!
		$contentType: CatchupContentType!
	) {
		catchupGetLastViewedTime(contentId: $contentId, contentType: $contentType) {
			lastViewedTimeISO
		}
	}
`;

const useDynamicSplitButtonContentHistoryQuery = graphql`
	query useDynamicSplitButtonContentHistoryQuery($contentId: ID!) {
		contentHistory(contentId: $contentId, first: 1) {
			nodes {
				collaborators {
					userAccountIds
				}
			}
		}
	}
`;

type DynamicSplitButtonReturnType = {
	showCatchupAsDefault: boolean;
	queryLoading: boolean;
	showSummarizeAsDefault?: boolean;
	queryError?: ApolloErrorInterop | undefined;
	readTime?: number;
};

export const useDynamicSplitButton = (
	contentId: string,
	contentType: CatchupContentType,
	isRefreshRovoButton: boolean = false,
): DynamicSplitButtonReturnType => {
	const { isAdminHubAIEnabled, userId } = useSessionData();
	const pageMode = useGetPageMode();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	const { readTime, loading: readTimeLoading } = useReadTime(
		contentId,
		!isAdminHubAIEnabled || pageMode === PageMode.EDIT,
	);

	const {
		data,
		loading: catchupLastViewedLoading,
		error: queryError,
		// eslint-disable-next-line react-hooks/rules-of-hooks
	} = useQueryInterop<
		useDynamicSplitButtonCatchupLastViewedTimeQuery$data,
		useDynamicSplitButtonCatchupLastViewedTimeQuery$variables
	>(useDynamicSplitButtonCatchupLastViewedTimeQuery, {
		variables: {
			contentId,
			contentType,
		},
		skip: !isAdminHubAIEnabled || pageMode === PageMode.EDIT,
	});

	const { data: lastModifiedDateData, loading: lastModifiedLoading } = useQueryInterop<
		useDynamicSplitButtonLastModifiedDateQuery$data,
		useDynamicSplitButtonLastModifiedDateQuery$variables
	>(
		graphql`
			query useDynamicSplitButtonLastModifiedDateQuery($contentId: ID!) {
				content(id: $contentId) {
					nodes {
						id
						metadata {
							lastModifiedDate
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
			skip: !isAdminHubAIEnabled || pageMode === PageMode.EDIT,
		},
	);

	const { data: contentHistoryData, loading: contentHistoryLoading } = useQueryInterop<
		useDynamicSplitButtonContentHistoryQuery$data,
		useDynamicSplitButtonContentHistoryQuery$variables
	>(useDynamicSplitButtonContentHistoryQuery, {
		variables: {
			contentId,
		},
		skip: !isAdminHubAIEnabled || pageMode === PageMode.EDIT || !isRovoButtonEnabled,
		fetchPolicy: 'network-only',
	});

	const lastModifiedDate =
		lastModifiedDateData?.content?.nodes?.[0]?.metadata?.lastModifiedDate ?? '';
	const lastViewedTime = data?.catchupGetLastViewedTime?.lastViewedTimeISO ?? '';
	const parsedLastViewedTime = Date.parse(lastViewedTime);
	const parsedLastModifiedDate = Date.parse(lastModifiedDate);
	const lastEditedCollaborators =
		contentHistoryData?.contentHistory?.nodes?.[0]?.collaborators?.userAccountIds ?? [];
	const isCurrentUserARecentCollaborator = lastEditedCollaborators.some(
		(collaborator?: string | null) => collaborator === userId,
	);

	const isFirstTimeVisit = Number.isNaN(parsedLastViewedTime);

	const queryLoading =
		catchupLastViewedLoading || lastModifiedLoading || contentHistoryLoading || readTimeLoading;

	if ((!parsedLastViewedTime && !isFirstTimeVisit) || !parsedLastModifiedDate || queryError) {
		return {
			showCatchupAsDefault: false,
			queryLoading,
			queryError,
		};
	}

	if (
		isRovoButtonEnabled &&
		pageMode !== PageMode.EDIT &&
		!queryLoading &&
		isFirstTimeVisit &&
		readTime &&
		readTime >= 5 &&
		!isCurrentUserARecentCollaborator
	) {
		if (isRefreshRovoButton && expValEquals('rovo_awareness_experiment', 'cohort', 'test')) {
			return {
				showCatchupAsDefault: false,
				showSummarizeAsDefault: true,
				readTime,
				queryLoading,
			};
		}
	}

	const showCatchupAsDefault =
		pageMode !== PageMode.EDIT &&
		!queryLoading &&
		parsedLastModifiedDate > parsedLastViewedTime &&
		!isCurrentUserARecentCollaborator;

	return {
		showCatchupAsDefault,
		showSummarizeAsDefault: false,
		queryLoading,
	};
};
